import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { useActualIncidentId } from './commonHooks';
import { useGetPositionsForIncident } from '@/hooks/incident/useRoster';

export type ResourceNeeds = {
  have: number;
  need: number;
  required: number;
  resourceName: string;
};

export type WorkAssignment = {
  id: string;
  work_assignment_id: string;
  incident_id: string;
  user_guid: string;
  version: number;
  branch?: string;
  branch_name?: string;
  division?: string;
  division_name?: string;
  work_assignment: string;
  overhead_positions?: string;
  reporting_location?: string;
  requested_arrival_time?: Date;
  special_instructions?: string;
  timestamp: Date;
  archived: boolean;
  geolocation: any;
  strike_teams: UUID[];
  resource_sets: UUID[];
  resource_details?: Array<{
    id: string;
    name: string;
    kind: string;
    type: string;
    quantity?: number;
    status?: string;
  }>;
  leader: string;
  leader_contact: string;
  leader_user_guid?: string;
  status: string;
  incident_tactic_id?: string;
  parent_tactics?: string[];
  utc_offset?: number;
  work_group_name?: string; // used in communications display
  work_group_id?: string; // used in communications display
  resource_list?: ResourceNeeds[];
  operational_period_number: number;
};

export const keys = {
  base: 'incident_workAssignments',
  get all() {
    return [keys.base];
  },
  list: (incidentId: string, userId: string) => [keys.base, incidentId, userId],
} as const;

export type CreateWorkAssignmentReqData = {
  work_assignment: string;
  status: string;
  operational_period_number: number;
  work_group_id?: UUID;
  work_assignment_id?: string;
  incident_id?: string;
  branch?: string;
  division?: string;
  overhead_positions?: string;
  reporting_location?: string;
  requested_arrival_time?: Date;
  special_instructions?: string;
  archived?: boolean;
  geolocation?: any;
  strike_teams?: UUID[];
  resource_sets?: UUID[];
  leader?: string;
  leader_contact?: string;
  incident_tactic_id?: string;
  parent_tactics?: string[];
  utc_offset?: number;
  resources?: any;
};

export const useWorkAssignments = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<WorkAssignment[]>(
        `${config.apiGateway.ics_work_assignments}/${incidentId}`
      );
      return resp.data;
    },
  });
};

export const useWorkAssignmentsForIncident = (
  incidentId: string,
  enabled = true
) => {
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<WorkAssignment[]>(
        `${config.apiGateway.ics_work_assignments}/${incidentId}`
      );
      return resp.data;
    },
    enabled: enabled && !!incidentId,
  });
};

export const useCreateWorkAssignment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CreateWorkAssignmentReqData) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignments}/${incidentId}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed."
        );
      } else {
        toast.error(error.message);
      }
    },
  });
  return mutation;
};

interface EditWorkAssignmentReqData {
  id: string;
  branch?: string;
  division?: string;
  work_assignment: string;
  resources?: string;
  overhead_positions?: string;
  reporting_location?: string;
  requested_arrival_time?: Date;
  special_instructions?: string;
  archived?: boolean;
  geolocation: any;
  strike_teams: UUID[];
  resource_sets: UUID[];
  leader: string;
  leader_contact: string;
  status: string;
  incident_tactic_id?: string;
  utc_offset?: number;
  work_group_name?: string;
  work_group_id?: UUID;
  resource_list?: any;
  operational_period_number: number;
}

export const useEditWorkAssignment = (workAssignmentId: string) => {
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditWorkAssignmentReqData) => {
      return await axios.put(
        `${config.apiGateway.ics_work_assignments}/${incidentId}/${workAssignmentId}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      // Also invalidate resources to update the resource cards
      queryClient.invalidateQueries({
        queryKey: ['resources-in-incident', incidentId],
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateWorkAssignment = () => {
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditWorkAssignmentReqData) => {
      return await axios.put(
        `${config.apiGateway.ics_work_assignments}/${incidentId}/${data.id}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
};

export const useGetWorkgroupsForIncident = () => {
  const { data: roster } = useGetPositionsForIncident();

  const workGroups =
    roster
      ?.filter((member) => member.is_workgroup)
      .map((wg) => ({ id: wg.id, name: wg.name })) || [];

  return workGroups;
};

export const useWorkgroupOptionsForIncident = () => {
  const workGroups = useGetWorkgroupsForIncident();

  const workGroupOptions = workGroups.map((group) => ({
    label: group.name,
    value: group.id,
  }));

  return workGroupOptions;
};
